"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Check, ArrowRight, X } from "lucide-react";
import { ManualPositionSetupStep } from "@/components/modals/manual-position-setup-step";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { trackClientEvent } from "@/lib/analyticsClient";
import { useSession } from "next-auth/react";
import { DEFAULT_INITIAL_BALANCE } from "@/lib/paperAccount";
import {
  flattenChainOptions, findOptionMid, formatPositionLabel,
  type ChainOptionQuote,
} from "@/lib/manualPositionEntry";

interface AddedPosition {
  id: string;
  symbol: string;
  isOption: boolean;
  side: "long" | "short";
  optionType?: "CALL" | "PUT";
  strike?: number;
  expiration?: string;
  units: number;
  price: number;
  tradeDate: string;
  currentPrice: number | null;
}

interface ManualPositionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
  existingPaperAccountId?: string | null;
}

type Step = "setup" | "positions";

const SELECTED_TAB_CLASS =
  "border-transparent bg-gradient-to-r from-emerald-500 to-green-600 text-white";
const UNSELECTED_TAB_CLASS =
  "border-border/50 text-muted-foreground hover:bg-muted/50";

export function ManualPositionModal({
  open,
  onOpenChange,
  onComplete,
  existingPaperAccountId,
}: ManualPositionModalProps) {
  const { data: session } = useSession();
  const [step, setStep] = useState<Step>(existingPaperAccountId ? "positions" : "setup");
  const [paperAccountId, setPaperAccountId] = useState<string | null>(existingPaperAccountId || null);

  // Step 1: Account setup
  const [nickname, setNickname] = useState("My Portfolio");
  const [balance, setBalance] = useState(DEFAULT_INITIAL_BALANCE.toString());
  const [setupLoading, setSetupLoading] = useState(false);

  // Step 2: Position entry
  const [assetType, setAssetType] = useState<"stock" | "option">("stock");
  const [ticker, setTicker] = useState("");
  const [side, setSide] = useState<"long" | "short">("long");
  const [optionType, setOptionType] = useState<"CALL" | "PUT">("CALL");
  const [strike, setStrike] = useState("");
  const [expiration, setExpiration] = useState("");
  const [units, setUnits] = useState("");
  const [price, setPrice] = useState("");
  const [tradeDate, setTradeDate] = useState(new Date().toISOString().split("T")[0]);
  const [addLoading, setAddLoading] = useState(false);
  const [deleteIfBrokerExists, setDeleteIfBrokerExists] = useState(true);
  const [addedPositions, setAddedPositions] = useState<AddedPosition[]>([]);
  const [completing, setCompleting] = useState(false);

  // Expiration fetching
  const [expirations, setExpirations] = useState<string[]>([]);
  const [expirationsLoading, setExpirationsLoading] = useState(false);
  const expDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Live price prefetching: underlying quote on ticker input, contract chain
  // once ticker + expiration + type are known.
  const [underlyingPrice, setUnderlyingPrice] = useState<number | null>(null);
  const [underlyingLoading, setUnderlyingLoading] = useState(false);
  const quoteDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [chainOptions, setChainOptions] = useState<ChainOptionQuote[]>([]);
  const [chainLoading, setChainLoading] = useState(false);

  useEffect(() => {
    setUnderlyingPrice(null);
    if (!ticker.trim()) {
      setUnderlyingLoading(false);
      return;
    }
    if (quoteDebounceRef.current) clearTimeout(quoteDebounceRef.current);
    setUnderlyingLoading(true);
    quoteDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/stocks/quote?ticker=${encodeURIComponent(ticker.trim())}`);
        if (res.ok) {
          const data = await res.json();
          setUnderlyingPrice(typeof data.price === "number" ? data.price : null);
        }
      } catch {} finally {
        setUnderlyingLoading(false);
      }
    }, 600);
    return () => { if (quoteDebounceRef.current) clearTimeout(quoteDebounceRef.current); };
  }, [ticker]);

  useEffect(() => {
    setChainOptions([]);
    if (assetType !== "option" || !ticker.trim() || !expiration) return;
    let cancelled = false;
    setChainLoading(true);
    (async () => {
      try {
        const res = await fetch(
          `/api/options/chain?ticker=${encodeURIComponent(ticker.trim())}&expiration=${expiration}&optionType=${optionType.toLowerCase()}`,
        );
        if (res.ok && !cancelled) {
          const data = await res.json();
          setChainOptions(flattenChainOptions(data.optionChains));
        }
      } catch {} finally {
        if (!cancelled) setChainLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [assetType, ticker, expiration, optionType]);

  const strikeNum = parseFloat(strike);
  const contractMid = useMemo(
    () => findOptionMid(chainOptions, strikeNum),
    [chainOptions, strikeNum],
  );
  const strikeMatched = useMemo(
    () => Number.isFinite(strikeNum) && chainOptions.some((o) => Math.abs(o.strike - strikeNum) < 0.01),
    [chainOptions, strikeNum],
  );
  // The live value of what's being entered: underlying for stock, contract mid for options.
  const liveValue = assetType === "stock" ? underlyingPrice : contractMid;

  useEffect(() => {
    if (assetType !== "option" || !ticker.trim() || ticker.trim().length < 1) {
      setExpirations([]);
      setExpiration("");
      return;
    }
    if (expDebounceRef.current) clearTimeout(expDebounceRef.current);
    expDebounceRef.current = setTimeout(async () => {
      setExpirationsLoading(true);
      try {
        const res = await fetch(`/api/options/expirations?ticker=${encodeURIComponent(ticker.trim())}`);
        if (res.ok) {
          const data = await res.json();
          setExpirations(data.expirations || []);
        } else {
          setExpirations([]);
        }
      } catch {
        setExpirations([]);
      } finally {
        setExpirationsLoading(false);
      }
    }, 500);
    return () => { if (expDebounceRef.current) clearTimeout(expDebounceRef.current); };
  }, [ticker, assetType]);

  useEffect(() => {
    if (open) {
      if (existingPaperAccountId) {
        setPaperAccountId(existingPaperAccountId);
        setStep("positions");
      } else {
        checkExistingAccount();
      }
    }
  }, [open, existingPaperAccountId]);

  const checkExistingAccount = useCallback(async () => {
    try {
      const res = await fetch("/api/broker/paper/status");
      if (res.ok) {
        const data = await res.json();
        if (data.account?.status === "ACTIVE") {
          setPaperAccountId(`MANUAL_JOURNAL_${data.account.id}`);
          setStep("positions");
        }
      }
    } catch {}
  }, []);

  const handleSetupSubmit = async () => {
    const bal = parseFloat(balance);
    if (isNaN(bal) || bal < 0) {
      toast.error("Please enter a valid balance");
      return;
    }
    setSetupLoading(true);
    try {
      const res = await fetch("/api/broker/paper/connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountName: nickname.trim() || "My Portfolio",
          initialBalance: bal,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create account");

      const accountId = `MANUAL_JOURNAL_${data.account.id}`;
      setPaperAccountId(accountId);
      setStep("positions");

      trackClientEvent({
        name: "Broker Connected",
        params: { type: "manual", broker_name: "Manual Positions", connection_provider: "Manual", accounts_count: 1, account_name: nickname.trim(), initial_balance: bal },
        userId: session?.user?.id ?? undefined,
      });
    } catch (err: any) {
      toast.error(err.message || "Failed to create account");
    } finally {
      setSetupLoading(false);
    }
  };

  const resetForm = () => {
    setTicker("");
    setSide("long");
    setOptionType("CALL");
    setStrike("");
    setExpiration("");
    setUnits("");
    setPrice("");
    setTradeDate(new Date().toISOString().split("T")[0]);
  };

  // Commits the current form to the journal. Returns the saved position, or
  // null when validation / the request failed (a toast is already shown).
  const submitPosition = async (): Promise<AddedPosition | null> => {
    if (!paperAccountId) return null;
    if (!ticker.trim()) { toast.error("Please enter a ticker"); return null; }
    if (!units || parseFloat(units) <= 0) { toast.error("Please enter a valid quantity"); return null; }
    if (!price || parseFloat(price) < 0) { toast.error("Please enter a valid cost basis"); return null; }
    if (assetType === "option") {
      if (!strike || parseFloat(strike) <= 0) { toast.error("Please enter a valid strike price"); return null; }
      if (!expiration) { toast.error("Please select an expiration"); return null; }
    }

    setAddLoading(true);
    try {
      // Current value: prefer the prefetched quote/chain, fall back to fetching.
      let currentPrice: number | null = null;

      if (assetType === "stock") {
        if (underlyingPrice !== null) {
          currentPrice = underlyingPrice;
        } else {
          const quoteRes = await fetch(`/api/stocks/quote?ticker=${encodeURIComponent(ticker.trim())}`);
          if (!quoteRes.ok) {
            throw new Error(`Ticker "${ticker.trim()}" not found. Please check the symbol.`);
          }
          const quoteData = await quoteRes.json();
          currentPrice = quoteData.price ?? null;
        }
      } else {
        let options = chainOptions;
        if (options.length === 0) {
          const chainRes = await fetch(
            `/api/options/chain?ticker=${encodeURIComponent(ticker.trim())}&expiration=${expiration}&optionType=${optionType.toLowerCase()}`,
          );
          if (!chainRes.ok) {
            throw new Error(`Could not fetch option chain for ${ticker.trim()}. Please check the symbol.`);
          }
          const chainData = await chainRes.json();
          options = flattenChainOptions(chainData.optionChains);
        }
        const strikeVal = parseFloat(strike);
        if (!options.some((o) => Math.abs(o.strike - strikeVal) < 0.01)) {
          throw new Error(`No ${optionType} option found at $${strike} strike for ${ticker.trim()} expiring ${expiration}.`);
        }
        currentPrice = findOptionMid(options, strikeVal);
      }

      let txType: string;
      if (assetType === "stock") {
        txType = side === "long" ? "BUY" : "SELL";
      } else {
        txType = side === "long" ? "BUY_TO_OPEN" : "SELL_TO_OPEN";
      }

      const body: any = {
        accountId: paperAccountId,
        type: txType,
        symbol: ticker.trim().toUpperCase(),
        isOption: assetType === "option",
        tradeDate: new Date(tradeDate).toISOString(),
        units: parseFloat(units),
        price: parseFloat(price),
        deleteIfBrokerExists,
      };

      if (assetType === "option") {
        body.optionStrike = parseFloat(strike);
        body.optionExpiration = new Date(expiration).toISOString();
        body.optionType = optionType;
      }

      const res = await fetch("/api/journal/transactions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to add position");

      const newPos: AddedPosition = {
        id: data.transaction?.id || Date.now().toString(),
        symbol: ticker.trim().toUpperCase(),
        isOption: assetType === "option",
        side,
        optionType: assetType === "option" ? optionType : undefined,
        strike: assetType === "option" ? parseFloat(strike) : undefined,
        expiration: assetType === "option" ? expiration : undefined,
        units: parseFloat(units),
        price: parseFloat(price),
        tradeDate,
        currentPrice,
      };

      trackClientEvent({
        name: "input_manual_position",
        params: {
          symbol: newPos.symbol,
          is_option: newPos.isOption,
          side: newPos.side,
          option_type: newPos.optionType,
        },
        userId: session?.user?.id ?? undefined,
      });

      return newPos;
    } catch (err: any) {
      toast.error(err.message || "Failed to add position");
      return null;
    } finally {
      setAddLoading(false);
    }
  };

  // "+ Add Leg": commit the current form and keep the modal open for the next one.
  const handleAddLeg = async () => {
    const pos = await submitPosition();
    if (!pos) return;
    setAddedPositions(prev => [pos, ...prev]);
    resetForm();
  };

  // Primary "Add": commit the current form (when filled) and finish.
  const handleAddAndDone = async () => {
    if (formHasInput) {
      const pos = await submitPosition();
      if (!pos) return;
      setAddedPositions(prev => [pos, ...prev]);
    }
    handleComplete();
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      // Lots are created directly by the API, no resync needed for instant visibility
    } finally {
      setCompleting(false);
      onOpenChange(false);
      onComplete?.();
    }
  };

  const handleClose = () => {
    if (addLoading || setupLoading) return;
    handleComplete();
  };

  const formHasInput = ticker.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-lg p-0 gap-0 border-0 bg-transparent shadow-none [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Add Positions Manually</DialogTitle>
        <div className="p-2 sm:p-3">
          <div className="rounded-lg overflow-hidden shadow-xl">
            <div className="h-1.5 rounded-t-lg bg-gradient-to-r from-transparent via-zinc-300 dark:via-zinc-600 to-transparent" />
            <div className="rounded-b-lg border border-t-0 border-border/50 bg-background shadow-lg">
              <div className="p-5 sm:p-6 space-y-4">

                {step === "setup" && (
                  <ManualPositionSetupStep
                    nickname={nickname}
                    onNicknameChange={setNickname}
                    balance={balance}
                    onBalanceChange={setBalance}
                    loading={setupLoading}
                    onSubmit={handleSetupSubmit}
                    onClose={handleClose}
                  />
                )}

                {step === "positions" && (
                  <>
                    <div className="relative text-center space-y-0.5">
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={addLoading}
                        className="absolute -right-2 -top-1 rounded-full p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <h2 className="text-lg tracking-tight" style={{ fontWeight: 600 }}>
                        Add your positions
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        Enter your current holdings. You can add more later.
                      </p>
                    </div>

                    {/* Asset type toggle */}
                    <div className="flex gap-2">
                      {(["stock", "option"] as const).map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setAssetType(type)}
                          className={`flex-1 text-sm py-1.5 rounded-md border transition-all capitalize ${
                            assetType === type ? SELECTED_TAB_CLASS : UNSELECTED_TAB_CLASS
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>

                    {/* Ticker + Side */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <div className="flex items-baseline justify-between">
                          <Label className="text-xs">Ticker</Label>
                          {ticker.trim() && (
                            <span className="text-[11px] tabular-nums text-muted-foreground">
                              {underlyingLoading
                                ? "…"
                                : underlyingPrice !== null
                                  ? `$${underlyingPrice.toFixed(2)}`
                                  : ""}
                            </span>
                          )}
                        </div>
                        <Input
                          placeholder="AAPL"
                          value={ticker}
                          onChange={(e) => setTicker(e.target.value.toUpperCase())}
                          disabled={addLoading}
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Side</Label>
                        <div className="flex gap-1.5">
                          <button
                            type="button"
                            onClick={() => setSide("long")}
                            className={`flex-1 text-xs py-2 rounded-md border transition-all ${
                              side === "long"
                                ? "border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                                : UNSELECTED_TAB_CLASS
                            }`}
                          >
                            Long
                          </button>
                          <button
                            type="button"
                            onClick={() => setSide("short")}
                            className={`flex-1 text-xs py-2 rounded-md border transition-all ${
                              side === "short"
                                ? "border-red-500 bg-red-500/10 text-red-600 dark:text-red-400"
                                : UNSELECTED_TAB_CLASS
                            }`}
                          >
                            Short
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Option-specific fields */}
                    {assetType === "option" && (
                      <>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs">Type</Label>
                            <div className="flex gap-1.5">
                              {(["CALL", "PUT"] as const).map((type) => (
                                <button
                                  key={type}
                                  type="button"
                                  onClick={() => setOptionType(type)}
                                  className={`flex-1 text-xs py-2 rounded-md border transition-all ${
                                    optionType === type ? SELECTED_TAB_CLASS : UNSELECTED_TAB_CLASS
                                  }`}
                                >
                                  {type === "CALL" ? "Call" : "Put"}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs">Strike</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.5"
                              placeholder="150.00"
                              value={strike}
                              onChange={(e) => setStrike(e.target.value)}
                              disabled={addLoading}
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs">Expiration</Label>
                            {expirationsLoading ? (
                              <div className="flex items-center gap-2 h-9 px-3 text-xs text-muted-foreground border rounded-md">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                Loading...
                              </div>
                            ) : !ticker.trim() ? (
                              <div className="flex items-center h-9 px-3 text-xs text-muted-foreground border rounded-md">
                                Ticker first
                              </div>
                            ) : expirations.length === 0 ? (
                              <div className="flex items-center h-9 px-3 text-xs text-muted-foreground border rounded-md">
                                None found
                              </div>
                            ) : (
                              <Select value={expiration} onValueChange={setExpiration} disabled={addLoading}>
                                <SelectTrigger className="h-9 text-xs">
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent className="max-h-60">
                                  {expirations.map((exp) => (
                                    <SelectItem key={exp} value={exp} className="text-xs">
                                      {exp}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </div>
                        {/* Live contract value */}
                        {expiration && strike && (
                          <div className="flex items-center justify-between rounded-md border border-border/50 bg-muted/30 px-2.5 py-1.5 text-[11px]">
                            {chainLoading ? (
                              <span className="flex items-center gap-1.5 text-muted-foreground">
                                <Loader2 className="h-3 w-3 animate-spin" /> Fetching contract value...
                              </span>
                            ) : contractMid !== null ? (
                              <>
                                <span className="text-muted-foreground tabular-nums">
                                  {ticker.trim()} ${strike}{optionType === "CALL" ? "C" : "P"} {expiration} · mid <span className="text-foreground">${contractMid.toFixed(2)}</span>
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setPrice(contractMid.toFixed(2))}
                                  className="text-emerald-600 dark:text-emerald-400 hover:underline shrink-0"
                                >
                                  Use as cost
                                </button>
                              </>
                            ) : (
                              <span className="text-muted-foreground">
                                {strikeMatched ? "No live quote for this contract" : "No contract at this strike"}
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Quantity, Cost Basis, Trade Date */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">
                          {assetType === "option" ? "Contracts" : "Shares"}
                        </Label>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          placeholder={assetType === "option" ? "1" : "100"}
                          value={units}
                          onChange={(e) => setUnits(e.target.value)}
                          disabled={addLoading}
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">
                          Cost / {assetType === "option" ? "contract" : "share"}
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={liveValue !== null ? liveValue.toFixed(2) : "5.00"}
                          value={price}
                          onChange={(e) => setPrice(e.target.value)}
                          disabled={addLoading}
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Trade Date</Label>
                        <Input
                          type="date"
                          value={tradeDate}
                          onChange={(e) => setTradeDate(e.target.value)}
                          disabled={addLoading}
                          className="h-9 px-1.5 text-xs [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                        />
                      </div>
                    </div>

                    <div className="flex items-start gap-2 p-2 rounded-md border border-border/50 bg-muted/30">
                      <Checkbox
                        id="delete-if-broker"
                        checked={deleteIfBrokerExists}
                        onCheckedChange={(checked) => setDeleteIfBrokerExists(checked === true)}
                        disabled={addLoading}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <label htmlFor="delete-if-broker" className="text-xs font-medium cursor-pointer leading-tight">
                          Auto-remove if matched in broker
                        </label>
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                          If this position later appears from your broker sync, this manual entry will be automatically hidden
                        </p>
                      </div>
                    </div>

                    {addedPositions.length > 0 && (
                      <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        <p className="text-xs text-muted-foreground">
                          {addedPositions.length} position{addedPositions.length !== 1 ? "s" : ""} added
                        </p>
                        {addedPositions.map((pos) => {
                          const pnlPerUnit = pos.currentPrice !== null
                            ? (pos.side === "short" ? pos.price - pos.currentPrice : pos.currentPrice - pos.price)
                            : null;
                          const multiplier = pos.isOption ? 100 : 1;
                          const totalPnl = pnlPerUnit !== null ? pnlPerUnit * pos.units * multiplier : null;

                          return (
                            <div
                              key={pos.id}
                              className="rounded-md border border-border/50 px-3 py-1.5 flex items-center justify-between gap-2"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                                <span className="text-xs truncate">
                                  {formatPositionLabel(pos)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2.5 text-[11px] shrink-0 tabular-nums">
                                <span className="text-muted-foreground">
                                  ${pos.price.toFixed(2)}
                                </span>
                                {pos.currentPrice !== null && (
                                  <>
                                    <span className="text-muted-foreground">
                                      → ${pos.currentPrice.toFixed(2)}
                                    </span>
                                    {totalPnl !== null && (
                                      <span className={totalPnl >= 0 ? "text-emerald-500" : "text-red-500"}>
                                        {totalPnl >= 0 ? "+" : ""}{totalPnl.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Saves the current leg and keeps the form open for another */}
                    <Button
                      variant="outline"
                      className="w-full h-9"
                      disabled={addLoading || completing || !formHasInput}
                      onClick={handleAddLeg}
                    >
                      {addLoading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="mr-2 h-4 w-4" />
                      )}
                      Add Leg
                    </Button>

                    {/* Saves the current leg (if any) and closes */}
                    <Button
                      size="lg"
                      className="w-full h-11 bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700"
                      style={{ fontWeight: 600 }}
                      disabled={addLoading || completing || (!formHasInput && addedPositions.length === 0)}
                      onClick={handleAddAndDone}
                    >
                      {(addLoading || completing) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {formHasInput ? "Add" : "Done"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>

                    {addedPositions.length === 0 && !formHasInput && (
                      <button
                        type="button"
                        className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
                        disabled={completing}
                        onClick={handleComplete}
                      >
                        Skip for now
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
